import { Link } from "react-router-dom";
import { ArrowUpRight, Database, Users, ShoppingCart, Receipt, Warehouse, Boxes, Cloud, Server } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const SOLUTIONS = [
  {
    slug: "s4hana",
    Icon: Database,
    name: "SAP S/4HANA Implementation",
    tagline: "The intelligent ERP suite.",
    desc: "End-to-end S/4HANA implementations on the deployment model that fits — covering Finance, Supply Chain, Manufacturing, and Procurement on a single in-memory platform.",
    pillars: [
      { Icon: Cloud, label: "SAP S/4HANA Private Cloud" },
      { Icon: Server, label: "SAP S/4HANA Public Cloud" },
    ],
  },
  {
    slug: "successfactors",
    Icon: Users,
    name: "SAP SuccessFactors",
    tagline: "Human experience management.",
    desc: "Cloud HCM that connects core HR, talent, learning, and people analytics — designed to elevate the employee experience at every touchpoint.",
    pillars: [{ label: "Employee Central" }, { label: "Recruiting & Onboarding" }, { label: "Performance & Goals" }, { label: "Learning" }],
  },
  {
    slug: "ariba",
    Icon: ShoppingCart,
    name: "SAP Ariba",
    tagline: "Intelligent spend management.",
    desc: "From sourcing to invoice — modernise procurement with the world's largest business network and a unified spend management suite.",
    pillars: [{ label: "Sourcing & Contracts" }, { label: "Buying & Invoicing" }, { label: "Supplier Management" }, { label: "Ariba Network" }],
  },
  {
    slug: "concur",
    Icon: Receipt,
    name: "SAP Concur",
    tagline: "Travel, expense, and invoice automated.",
    desc: "Integrated travel, expense, and invoice management — automate policy, capture spend, and close the books faster.",
    pillars: [{ label: "Concur Expense" }, { label: "Concur Travel" }, { label: "Concur Invoice" }, { label: "Compliance & analytics" }],
  },
  {
    slug: "ewm",
    Icon: Warehouse,
    name: "SAP Extended Warehouse Management",
    tagline: "Run a world-class warehouse.",
    desc: "Real-time visibility, labour and slotting optimisation, and yard management on SAP EWM — embedded or decentralised, on S/4HANA.",
    pillars: [{ label: "Inbound & outbound" }, { label: "Labour management" }, { label: "Yard management" }, { label: "MFS / automation" }],
  },
  {
    slug: "plm",
    Icon: Boxes,
    name: "SAP Product Lifecycle Management",
    tagline: "Design, develop, deliver — connected.",
    desc: "Manage the full product lifecycle from idea to retirement — connecting engineering, manufacturing, and quality on a single SAP backbone.",
    pillars: [{ label: "Document & BoM" }, { label: "Engineering change" }, { label: "Variant configuration" }, { label: "Quality management" }],
  },
];

export default function Solutions() {
  return (
    <div data-testid="solutions-page">
      <section className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <div className="overline">Solutions</div>
            <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[1.05] mt-6 text-[#0F172A]">
              The SAP portfolio, <br />curated for your business.
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-lg text-gray-600 leading-relaxed">
              From the digital core to the edges of your operating model — we architect SAP solutions that age well, scale gracefully, and stay clean.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24" data-testid="solutions-list">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-16">
          {SOLUTIONS.map((s, i) => (
            <Reveal key={s.slug} delay={i * 60}>
              <article
                data-testid={`solution-detail-${s.slug}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-10 border-t border-[#111827] pt-10"
              >
                <div className="md:col-span-3">
                  <div className="font-mono text-sm text-gray-500">0{i + 1}</div>
                  <s.Icon size={36} strokeWidth={1.3} className="mt-6 text-[#111827]" />
                </div>
                <div className="md:col-span-6">
                  <div className="overline text-gray-500">{s.tagline}</div>
                  <h2 className="font-serif text-3xl sm:text-4xl text-[#0F172A] leading-tight mt-3">{s.name}</h2>
                  <p className="text-gray-600 mt-5 text-lg leading-relaxed">{s.desc}</p>
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[15px] text-gray-700">
                    {s.pillars.map((p, pi) => {
                      const PIcon = p.Icon;
                      return (
                        <div key={pi} className="flex items-start gap-3">
                          {PIcon ? (
                            <PIcon size={16} strokeWidth={1.6} className="text-[#C9A24E] mt-1" />
                          ) : (
                            <span className="block w-1.5 h-1.5 bg-[#111827] mt-2" />
                          )}
                          <span>{p.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="md:col-span-3 md:text-right">
                  <Link
                    to="/contact"
                    data-testid={`solution-cta-${s.slug}`}
                    className="link-underline inline-flex items-center gap-2 text-[#111827] font-medium"
                  >
                    Discuss this solution <ArrowUpRight size={16} />
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-8">
            <div className="overline text-gray-400">Not sure which fits?</div>
            <h2 className="font-serif text-4xl sm:text-5xl mt-5 leading-tight">
              Bring us your business problem. We will map it to the right SAP solutions.
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" data-testid="solutions-bottom-cta" className="inline-flex items-center gap-3 bg-white text-[#0F172A] px-7 py-4 text-sm font-medium hover:bg-gray-100 transition">
              Speak with an architect <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
